/**
 * Formatters BR de billing — usados em `PaymentPendingPage` (PR-M2-00)
 * e `PlanPage` (PR-M2-02). Extraídos pra cá quando o segundo caller
 * apareceu — antes viviam duplicados na PaymentPendingPage.
 *
 * Decisão: não usar `date-fns` ou bibliotecas de moeda — Intl nativo
 * do JS faz o que precisamos sem +bundle. Mesma postura de
 * `features/leads/formatters.ts` (BR-locale-específicos).
 */

/** Formata número como BRL pt-BR.
 * @example formatBRL(199)     // "R$ 199,00"
 * @example formatBRL(1234.5)  // "R$ 1.234,50"
 */
export function formatBRL(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Formata `YYYY-MM-DD` ou ISO em data pt-BR.
 *
 * Importante: trata `YYYY-MM-DD` como **local** (não UTC), pra evitar
 * shift de 1 dia em timezones a oeste de UTC. Banco salva `due_date`
 * como `date` (sem tempo) — interpretar como UTC midnight resultaria
 * em "2026-04-25" virando "2026-04-24" em BRT. Local construction
 * resolve.
 *
 * @example formatDateBR("2026-04-25")             // "25/04/2026"
 * @example formatDateBR("2026-04-25T13:38:00Z")   // "25/04/2026"
 */
export function formatDateBR(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const datePart = iso.split('T')[0];
    if (!datePart) return null;
    const parts = datePart.split('-').map(Number);
    const [y, m, d] = parts;
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
  } catch {
    return null;
  }
}

/** Versão "longa" de `formatDateBR` — ex: "25 de abril de 2026". Útil
 * em cabeçalhos de cobrança onde a clareza importa mais que o
 * compactness. */
export function formatDateBRLong(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const datePart = iso.split('T')[0];
    if (!datePart) return null;
    const parts = datePart.split('-').map(Number);
    const [y, m, d] = parts;
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}
